import { useState } from "react";
import axios from "axios";
import { Package, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Reusable Archive button — sits NEXT TO existing Print buttons (never merged).
 *
 * Props:
 *   - taxYear:     number (required) — used in title + storage key
 *   - category:    string (required) — short label e.g. "Tax Prep", "Income",
 *                  "Bank Statement", "CACFP Allocation"
 *   - source:      string (required) — canonical API path the archive points at
 *                  (the backend re-renders the PDF from this source so the
 *                  archived bytes ALWAYS match what the user is looking at).
 *   - sourceParams: object — extra params (document_type, transaction_ids,
 *                   fiscal_year, etc.) — passed through verbatim.
 *   - titleSuffix: string (optional) — appended after the auto-generated title,
 *                  e.g. "16 items" or "FY 2026" for context-rich titles.
 *   - size:        "sm" | "md" — visual sizing. Defaults to "sm" to fit toolbars.
 *   - disabled:    boolean
 *   - testId:      string — extra data-testid suffix (otherwise auto)
 *   - onArchived:  () => void — optional callback after successful archive
 */
export default function ArchiveButton({
  taxYear,
  category,
  source,
  sourceParams = {},
  titleSuffix = "",
  size = "sm",
  disabled = false,
  testId,
  label,
  onArchived,
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Auto-generated title: "<Category> — Tax Year <YYYY>[ — <suffix>]"
  const autoTitle = `${category} — Tax Year ${taxYear}${titleSuffix ? ` — ${titleSuffix}` : ""}`;
  const safeTestId = testId || category.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  const openModal = () => {
    if (disabled) return;
    setNote("");
    setOpen(true);
  };

  const archive = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/archive`,
        {
          tax_year: Number(taxYear),
          category,
          title: autoTitle,
          source,
          source_params: sourceParams,
          optional_note: note.trim(),
        },
        { withCredentials: true }
      );
      toast.success("Archived", {
        description: autoTitle,
      });
      setOpen(false);
      if (typeof onArchived === "function") onArchived();
      // Let any listening surface (e.g. Archive page) refresh
      try { window.dispatchEvent(new CustomEvent("archive:changed")); } catch { /* ignore */ }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to archive";
      toast.error("Archive failed", { description: String(msg) });
    } finally {
      setSaving(false);
    }
  };

  const btnClass =
    size === "sm"
      ? "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-all"
      : "inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-all";

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        disabled={disabled}
        className={btnClass + (disabled ? " opacity-50 cursor-not-allowed" : "")}
        title={`Archive this ${category} report`}
        data-testid={`archive-btn-${safeTestId}`}
      >
        <Package className={size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"} />
        <span>{label || "Archive"}</span>
        <span className="text-amber-700/70 text-[10px]">▾</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          onClick={() => !saving && setOpen(false)}
          data-testid={`archive-modal-${safeTestId}`}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Package className="w-4 h-4 text-amber-700" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
                    Archive This Report
                  </h3>
                  <p className="text-xs text-slate-500">
                    Saves a permanent PDF copy in your Archive.
                  </p>
                </div>
              </div>
              <button
                onClick={() => !saving && setOpen(false)}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Close"
                data-testid={`archive-modal-close-${safeTestId}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Title</p>
                <p className="text-sm font-semibold text-slate-900 mt-1" data-testid={`archive-modal-title-${safeTestId}`}>
                  {autoTitle}
                </p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  Optional note
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Sent to accountant, March 2026"
                  className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-300 focus:outline-none resize-none"
                  rows={3}
                  maxLength={500}
                  data-testid={`archive-modal-note-${safeTestId}`}
                />
                <p className="text-[10px] text-slate-400 mt-1">{note.length}/500</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setOpen(false)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid={`archive-modal-cancel-${safeTestId}`}
              >
                Cancel
              </button>
              <button
                onClick={archive}
                disabled={saving}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-amber-600 hover:bg-amber-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
                data-testid={`archive-modal-confirm-${safeTestId}`}
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
                {saving ? "Archiving…" : "Archive"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
